import Axios, { AxiosResponse } from 'axios'
import { IMobileIdAuthResponse } from '../interfaces/i-mobileid'
import {
	authenticationServiceCancelToken,
	getHeadersWithJwtToken,
} from '../utils/auth-util'
import { IUserData } from '../interfaces/i-user-data'
import { SmartIdInitiateAuthDto } from '../interfaces/i-smart-id'
import { ISignicatIdAuthResponse } from '../interfaces/i-signicatid'
import { IBankIdStartResponse } from '../interfaces/i-bankid'
import {
	getNorwegianBankIdReturnUrl,
	getReturnUrl,
	getSignicatReturnUrl,
	geteParakstsReturnUrl,
} from '../utils/authHelpers'
import { apiRoutes } from 'shared/index'
import { TFunction } from 'i18next'
import appConfig from 'app.config'
import AuthMethodType from 'auth/constants/authMethods'

import { setupInterceptorsFor } from 'shared/Axios/Interceptors/Interceptors'
import {
	IApplicationOtpStartRequest,
	IGermanyOtpStartRequest,
	ILoanOtpStartRequest,
	IOtpCompleteRequest,
	IOtpStartResponse,
} from 'auth/interfaces/i-otpid'

const transportWithCredentials = setupInterceptorsFor(Axios.create({}))

const authenticationService = {
	async smartIdAuthInit(authRequest: {
		countryCode: string
		nationalIdentityNumber: string
		returnUrl: string
	}) {
		const { data } =
			await transportWithCredentials.post<SmartIdInitiateAuthDto>(
				apiRoutes.smartIdAuthInitiate,
				authRequest,
			)

		return data
	},

	async smartIdAuthComplete(
		authRequest: SmartIdInitiateAuthDto,
		retryCount: number = 0,
	): Promise<{ redirectUrl: string; secret: string }> {
		try {
			const config = {
				cancelToken: authenticationServiceCancelToken.getToken(),
			}
			console.log('retryC: ', retryCount)
			const { data } = await transportWithCredentials.post<{
				redirectUrl: string
				secret: string
			}>(apiRoutes.smartIdAuthComplete, authRequest, config)

			return data
		} catch (e) {
			// The request was made but no response was received
			// `error.request` is an instance of XMLHttpRequest in the browser and an instance of
			if ((e as any).request) {
				if (retryCount < 3) {
					return await this.smartIdAuthComplete(
						authRequest,
						retryCount + 1,
					)
				}
			}

			throw e
		}
	},

	async mobileIdAuthInitiate(
		phoneNumber: string,
		personalNumber: string,
		returnUrl: string,
	) {
		phoneNumber = phoneNumber.replace(/\s+/g, '')
		const { data } =
			await transportWithCredentials.post<IMobileIdAuthResponse>(
				apiRoutes.mobileIdAuthInitiate,
				{
					phoneNumber,
					returnUrl,
					personalNumber,
				},
			)

		return data
	},

	async mobileIdAuthComplete(userData: IUserData | undefined, t: TFunction) {
		const config = {
			headers: await getHeadersWithJwtToken(),
			cancelToken: authenticationServiceCancelToken.getToken(),
		}
		const { data } = await transportWithCredentials.post<{
			redirectUrl: string
			secret: string
		}>(apiRoutes.mobileIdAuthComplete, userData, config)

		if (!data) {
			throw new Error(t('AuthenticationError'))
		}

		return data
	},

	async cardIdAuthenticate(
		requestId: string,
		certificateContent: string,
		t: TFunction,
	) {
		const { data } = await transportWithCredentials.post(
			apiRoutes.idCardAuth(requestId),
			{ certificateContent },
		)

		return data as string
	},

	async signicatRedirectLinkGet() {
		const route =
			apiRoutes.signicatAuthLink +
			`?state=noState&locale=${
				appConfig.country
			}&callbackUrl=${getSignicatReturnUrl()}`

		const { data } = await transportWithCredentials.get<string>(route)
		return data
	},

	async signicatMitIdRedirectLinkGet() {
		const route =
			apiRoutes.signicatAuthMitId +
			`?state=noState&callbackUrl=${getSignicatReturnUrl()}`

		const { data } = await transportWithCredentials.get<string>(route)
		return data
	},

	async signicatResultGet(queryParams: string, t: TFunction) {
		const data =
			await transportWithCredentials.get<ISignicatIdAuthResponse>(
				apiRoutes.signicatAuthResult +
					`${queryParams}&callbackUrl=${getSignicatReturnUrl()}`,
			)

		if (data.status === 200) {
			return data.data
		}
		throw new Error(t('AuthenticationError'))
	},

	async norwegianBankIdRedirectLinkGet(authMethod: AuthMethodType) {
		let method = undefined
		switch (authMethod) {
			case AuthMethodType.norwegianBankId:
				method = 'nbid-high'
				break
			case AuthMethodType.norwegianBankIdBiometric:
				method = 'nbid-biometric'
				break
			case AuthMethodType.norwegianBankIdMobile:
				method = 'nbid-mobil'
				break
			default:
				throw new Error('Invalid authentication method')
		}

		const route =
			apiRoutes.norwegianBankIdAuthLink +
			`?state=noState&locale=${
				appConfig.country
			}&callbackUrl=${getNorwegianBankIdReturnUrl()}&method=${method}`
		const { data } = await transportWithCredentials.get<string>(route)
		return data
	},

	async norwegianBankIdResultGet(queryParams: string, t: TFunction) {
		const data =
			await transportWithCredentials.get<ISignicatIdAuthResponse>(
				apiRoutes.norwegianBankIdAuthResult +
					`${queryParams}&callbackUrl=${getNorwegianBankIdReturnUrl()}`,
			)
		if (data.status === 200) {
			return data.data
		}
		throw new Error(t('AuthenticationError'))
	},

	async norwegianBankIdApplicationResultGet(
		queryParams: string,
		t: TFunction,
	) {
		const data =
			await transportWithCredentials.get<ISignicatIdAuthResponse>(
				apiRoutes.norwegianBankIdApplicationAuthResult +
					`${queryParams}&callbackUrl=${getNorwegianBankIdReturnUrl()}`,
			)
		if (data.status === 200) {
			return data.data
		}
		throw new Error(t('AuthenticationError'))
	},

	async bankIdv2AuthInitiate(redirectUrl: string) {
		const response =
			await transportWithCredentials.get<IBankIdStartResponse>(
				apiRoutes.bankIdv2AuthInitiate + `?returnUrl=${redirectUrl}`,
			)
		return response.data
	},

	async bankIdv2AuthCancel(orderRef: string, t: TFunction) {
		const resp = await transportWithCredentials.post<any>(
			apiRoutes.bankIdv2AuthCancel,
			{
				orderRef,
			},
		)

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}

		return resp.status
	},

	async bankIdv2AuthCollect(
		orderRef: string,
		isAutoStart: boolean,
		t: TFunction,
	) {
		const config = {
			cancelToken: authenticationServiceCancelToken.getToken(),
		}
		const resp = await transportWithCredentials.post<{
			status: string
			isCompleted: boolean
			secret: string
			ssn: string
		}>(
			apiRoutes.bankIdv2AuthCollect,
			{
				orderRef,
				isAutoStart,
			},
			config,
		)

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}

		return resp.data
	},

	async bankIdv2GenerateQrCode(orderRef: string, t: TFunction) {
		const resp = await transportWithCredentials.post<{
			qrData: string
		}>(apiRoutes.bankIdv2GenerateQrCode, {
			orderRef,
		})

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}

		return resp.data
	},

	async getArtifact(requestId: string, taskId: string, t: TFunction) {
		let route
		switch (appConfig.country) {
			case 'dk':
				route = apiRoutes.getArtifactDk(requestId, taskId)
				break
			case 'fi':
				route = apiRoutes.getArtifactFi(requestId, taskId)
				break
			case 'no':
				route = apiRoutes.getArtifactBbBank(requestId, taskId)
				break
			default:
				route = apiRoutes.getArtifactFi(requestId, taskId)
		}
		console.log(route)
		const artifact = await transportWithCredentials.get<string>(route)

		if (artifact.status === 200) {
			return artifact.data
		}
		throw new Error(t('NoArtifact'))
	},

	async authenticate(
		apiRoute: string,
		ssn: string,
		birthDate: string,
		applicationId: string,
		t: TFunction,
	) {
		const resp = await transportWithCredentials.post<{
			secret: string
			returnUrl: string
		}>(apiRoute, {
			ssn,
			birthDate,
			applicationId,
			returnUrl: getReturnUrl(),
		})

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}
		console.log(resp.data)
		return resp.data
	},

	async germanyOtpStart(request: IGermanyOtpStartRequest, t: TFunction) {
		const route = (() => {
			switch (appConfig.country) {
				case 'de':
					return apiRoutes.germanyOtpStart
				case 'at':
					return apiRoutes.austriaOtpStart
				case 'es':
					return apiRoutes.spainOtpStartSsp
				case 'it':
					return apiRoutes.italyOtpStartSsp
				default:
					return apiRoutes.germanyOtpStart
			}
		})()

		const resp = await transportWithCredentials.post<{
			secret: string
			customerId: string
		}>(route, request)

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}
		console.log(resp.data)
		return resp.data
	},

	async germanyOtpComplete(
		secret: string,
		otp: string,
		customerId: string,
		t: TFunction,
	) {
		const route = (() => {
			switch (appConfig.country) {
				case 'de':
					return apiRoutes.germanyOtpComplete
				case 'at':
					return apiRoutes.austriaOtpComplete
				case 'es':
					return apiRoutes.spainOtpCompleteSsp
				case 'it':
					return apiRoutes.italyOtpCompleteSsp
				default:
					return apiRoutes.germanyOtpComplete
			}
		})()

		const resp = await transportWithCredentials.post<{
			redirectUrl: string
			secret: string
		}>(route, {
			secret,
			customerId,
			otp,
			returnUrl: getReturnUrl(),
		})

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}
		console.log(resp.data)
		return resp.data
	},

	async otpStart(request: IApplicationOtpStartRequest, t: TFunction) {
		const route = () => {
			switch (appConfig.country) {
				case 'de':
					return apiRoutes.germanyOtpStart
				case 'at':
					return apiRoutes.austriaOtpStart
				case 'es':
				case 'it':
					return apiRoutes.spainOtpStart
				default:
					return apiRoutes.germanyOtpStart
			}
		}

		const resp = await transportWithCredentials.post<{
			secret: string
			customerId: string
		}>(route(), request)

		return resp.data
	},

	async otpLoanStart(request: ILoanOtpStartRequest, t: TFunction) {
		const resp = await transportWithCredentials.post<
			ILoanOtpStartRequest,
			AxiosResponse<IOtpStartResponse>
		>(apiRoutes.loanOtpStart, request)

		if (resp.status !== 200) {
			throw new Error(t('AuthenticationError'))
		}

		return resp.data
	},

	async otpComplete(request: IOtpCompleteRequest, t: TFunction) {
		const route = () => {
			switch (appConfig.country) {
				case 'de':
					return apiRoutes.germanyOtpComplete
				case 'at':
					return apiRoutes.austriaOtpComplete
				case 'ee':
				case 'lv':
				case 'lt':
				case 'it':
				case 'es':
					return apiRoutes.spainOtpComplete
				default:
					return apiRoutes.germanyOtpComplete
			}
		}
		const config = {
			cancelToken: authenticationServiceCancelToken.getToken(),
		}
		const resp = await transportWithCredentials.post<{
			redirectUrl: string
			secret: string
		}>(route(), request, config)

		return resp.data
	},

	async electronicIDAuthorizationToken(
		applicationId: string,
		product: string,
	) {
		const { data } = await transportWithCredentials.get<{
			id: string
			authorization: string
		}>(apiRoutes.electronicIDToken(applicationId, product))
		console.log('received auth: ', data)
		return data
	},

	async electronicIDVerificationRequest(videoId: string, product: string) {
		const { data } = await transportWithCredentials.post(
			apiRoutes.electronicIDVerificationRequest,
			{ videoId: videoId, product: product },
			await getHeadersWithJwtToken(),
		)
		return data
	},

	redirectTo(url: string, secretValue: string) {
		const form = document.createElement('form')
		form.style.display = 'none'
		form.action = apiRoutes.redirectTo
		form.method = 'POST'

		var secret = document.createElement('input')
		secret.name = 'secret'
		secret.value = secretValue

		var redirectUrl = document.createElement('input')
		redirectUrl.name = 'redirectUrl'
		redirectUrl.value = url

		form.appendChild(secret)
		form.appendChild(redirectUrl)

		document.body.appendChild(form)
		form.submit()
	},

	async eParakstsRedirectLinkGet() {
		const route =
			apiRoutes.eParakstsAuthLink +
			`?state=noState&locale=${
				appConfig.country
			}&callbackUrl=${geteParakstsReturnUrl()}`
		const { data } = await transportWithCredentials.get<string>(route)
		return data
	},

	async eParakstsResultGet(queryParams: string, t: TFunction) {
		const data =
			await transportWithCredentials.get<ISignicatIdAuthResponse>(
				apiRoutes.eParakstsAuthResult +
					`${queryParams}&callbackUrl=${geteParakstsReturnUrl()}`,
			)

		if (data.status === 200) {
			return data.data
		}
		throw new Error(t('AuthenticationError'))
	},
}

export default authenticationService
